'use client';
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, MessageSquare, ChevronDown, ChevronUp, User, Building2, Calendar, FileText, X, Search, Loader2, AlertCircle, Download } from 'lucide-react';
import { thesisService } from '@/lib/services/thesisService';
import { useAuth } from '@/contexts/AuthContext';

interface Props {
  onCountChange?: (count: number) => void;
}

const ELSEVIER_KW_OPTIONS = [
  'Machine Learning', 'Artificial Intelligence', 'Deep Learning', 'Reinforcement Learning',
  'Computer Networks', 'Cybersecurity', 'Blockchain Technology', 'Internet of Things',
  'Business Management', 'Finance', 'Corporate Governance', 'ESG', 'Investment Portfolio',
  'EU Law', 'Privacy Law', 'Cryptocurrency', 'AML', 'FinTech',
  'Urban Planning', 'Cultural Heritage', 'Sustainable Design', 'Architecture',
  'Autonomous Systems', 'Robotics', 'Signal Processing', 'Sensor Fusion',
  'Risk Management', 'Compliance', 'Regulatory Framework', 'Post-Conflict Studies',
  'Participatory Design', 'Heritage Preservation', 'Smart Cities',
];

export default function PendingQueue({ onCountChange }: Props) {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [sendBackId, setSendBackId] = useState<string | null>(null);
  const [sendBackComment, setSendBackComment] = useState('');
  const [editingKeywordsId, setEditingKeywordsId] = useState<string | null>(null);
  const [editedKeywords, setEditedKeywords] = useState<Record<string, string[]>>({});
  const [kwSearch, setKwSearch] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    loadPending();
  }, []);

  const loadPending = async () => {
    setLoading(true);
    try {
      const data = await thesisService.getPending();
      setSubmissions(data);
      onCountChange?.(data.length);
      if (data.length > 0) setExpandedId(data[0].id);
    } catch (err) {
      console.log('Load pending error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getKeywordsForSub = (id: string) => {
    if (editedKeywords[id]) return editedKeywords[id];
    const sub = submissions.find(s => s.id === id);
    return sub?.thesis_keywords?.map((k: any) => k.keyword_label) || [];
  };

  const toggleEditedKeyword = (subId: string, kw: string) => {
    const current = getKeywordsForSub(subId);
    setEditedKeywords(prev => ({
      ...prev,
      [subId]: current.includes(kw) ? current.filter((k: string) => k !== kw) : [...current, kw],
    }));
  };

  const handleApprove = async (id: string) => {
    if (!user) return;
    setIsProcessing(true);
    setApprovingId(id);
    try {
      const updatedKws = editedKeywords[id];
      const result = await thesisService.approve(id, user.id, updatedKws);
      if (result) {
        const updated = submissions.filter(s => s.id !== id);
        setSubmissions(updated);
        onCountChange?.(updated.length);
        setExpandedId(null);
        toast.success('Thesis approved and published to the public repository.', { duration: 4000 });
      } else {
        toast.error('Failed to approve thesis. Please try again.');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to approve thesis.');
    } finally {
      setIsProcessing(false);
      setApprovingId(null);
    }
  };

  const handleSendBack = async (id: string) => {
    if (!sendBackComment.trim()) {
      toast.error('Please provide a comment explaining what needs to be revised.');
      return;
    }
    setIsProcessing(true);
    try {
      const sub = submissions.find(s => s.id === id);
      const result = await thesisService.sendBack(id, sendBackComment);
      if (result) {
        const updated = submissions.filter(s => s.id !== id);
        setSubmissions(updated);
        onCountChange?.(updated.length);
        setSendBackId(null);
        setSendBackComment('');
        setExpandedId(null);
        // Build notification message listing the emails
        const emails = new Set<string>();
        if (sub?.student?.email) emails.add(sub.student.email);
        if (sub?.student_email) emails.add(sub.student_email);
        const emailList = [...emails].join(', ');
        toast.success(
          emailList
            ? `Submission sent back. Notification sent to: ${emailList}`
            : 'Submission sent back to student with your comments.',
          { duration: 5000 }
        );
      } else {
        toast.error('Failed to send back thesis. Please try again.');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send back thesis.');
    } finally {
      setIsProcessing(false);
    }
  };

  const filteredKws = ELSEVIER_KW_OPTIONS.filter(k =>
    !kwSearch || k.toLowerCase().includes(kwSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-[#1a2e5a]" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-700 text-[#0f1a35]">Pending Approval Queue</h2>
          <p className="text-sm text-[#4a5578] mt-0.5">
            <span className="font-600 text-[#1a2e5a]">{submissions.length}</span> submissions awaiting your review
          </p>
        </div>
        {submissions.length > 0 && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <AlertCircle size={14} className="text-amber-600" />
            <span className="text-xs text-amber-800 font-600">
              Oldest: {submissions[submissions.length - 1]?.created_at?.slice(0, 10)}
            </span>
          </div>
        )}
      </div>

      {submissions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white border border-[#e2e6f0] rounded-2xl text-center">
          <CheckCircle2 size={40} className="text-emerald-500 mb-4" />
          <h3 className="text-lg font-600 text-[#0f1a35] mb-2">All caught up!</h3>
          <p className="text-sm text-[#4a5578]">No pending submissions. All theses have been reviewed.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.map((sub) => {
            const isExpanded = expandedId === sub.id;
            const isSendingBack = sendBackId === sub.id;
            const isEditingKw = editingKeywordsId === sub.id;
            const currentKws = getKeywordsForSub(sub.id);
            const originalKws = sub.thesis_keywords?.map((k: any) => k.keyword_label) || [];
            const hasKwEdits = JSON.stringify([...currentKws].sort()) !== JSON.stringify([...originalKws].sort());
            const studentName = sub.student?.full_name || 'Unknown Student';
            const studentEmail = sub.student?.email || '';
            const titleDisplay = sub.primary_lang === 'en' ? sub.title_primary : (sub.title_secondary || sub.title_primary);

            return (
              <div key={sub.id} className="bg-white border border-[#e2e6f0] rounded-2xl overflow-hidden transition-all duration-200 hover:shadow-sm">
                {/* Submission header */}
                <div
                  className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-[#1a2e5a]/2 transition-all"
                  onClick={() => setExpandedId(isExpanded ? null : sub.id)}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${sub.doc_type === 'phd' ? 'bg-purple-50 border border-purple-200' : 'bg-blue-50 border border-blue-200'}`}>
                      <FileText size={16} className={sub.doc_type === 'phd' ? 'text-purple-600' : 'text-blue-600'} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={sub.doc_type === 'phd' ? 'badge-phd' : 'badge-master'}>
                          {sub.doc_type === 'phd' ? 'PhD' : "Master's"}
                        </span>
                        <span className="text-xs font-mono-data text-[#8892a8]">{sub.id?.slice(0, 8)}</span>
                        {hasKwEdits && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-600 bg-amber-50 text-amber-700 border border-amber-200">
                            Keywords edited
                          </span>
                        )}
                      </div>
                      <h3 className="text-sm font-600 text-[#0f1a35] truncate">{titleDisplay}</h3>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-[#8892a8]">
                        <span className="flex items-center gap-1"><User size={10} />{studentName}</span>
                        <span className="flex items-center gap-1"><Building2 size={10} />{sub.department}</span>
                        <span className="flex items-center gap-1"><Calendar size={10} />{sub.created_at?.slice(0, 10)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    <div className="flex items-center gap-1.5 text-xs text-[#8892a8]">
                      <span className={`w-2 h-2 rounded-full ${sub.pdf_en_path ? 'bg-emerald-500' : 'bg-gray-300'}`} />EN PDF
                      <span className={`w-2 h-2 rounded-full ml-1 ${sub.pdf_el_path ? 'bg-emerald-500' : 'bg-gray-300'}`} />EL PDF
                    </div>
                    {isExpanded ? <ChevronUp size={16} className="text-[#8892a8]" /> : <ChevronDown size={16} className="text-[#8892a8]" />}
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="border-t border-[#e2e6f0] px-5 py-5 space-y-5">
                    {/* Abstracts */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      {sub.abstract_primary && (
                        <div>
                          <p className="text-xs font-600 text-[#4a5578] uppercase tracking-wide mb-1.5">
                            Abstract ({sub.primary_lang === 'en' ? 'English' : 'Greek'})
                          </p>
                          <p className="text-xs text-[#4a5578] leading-relaxed line-clamp-4">{sub.abstract_primary}</p>
                        </div>
                      )}
                      {sub.abstract_secondary && (
                        <div>
                          <p className="text-xs font-600 text-[#4a5578] uppercase tracking-wide mb-1.5">
                            Abstract ({sub.primary_lang === 'en' ? 'Greek' : 'English'})
                          </p>
                          <p className="text-xs text-[#4a5578] leading-relaxed line-clamp-4">{sub.abstract_secondary}</p>
                        </div>
                      )}
                    </div>

                    {/* Metadata grid */}
                    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 text-xs">
                      <div><p className="text-[#8892a8] font-600 uppercase tracking-wide mb-0.5">Student ID</p><p className="text-[#0f1a35] font-mono-data">{sub.student_id_number || '—'}</p></div>
                      <div><p className="text-[#8892a8] font-600 uppercase tracking-wide mb-0.5">Student Email</p><p className="text-[#0f1a35]">{sub.student_email || '—'}</p></div>
                      <div><p className="text-[#8892a8] font-600 uppercase tracking-wide mb-0.5">Registered Email</p><p className="text-[#0f1a35]">{studentEmail || '—'}</p></div>
                      <div><p className="text-[#8892a8] font-600 uppercase tracking-wide mb-0.5">Author</p><p className="text-[#0f1a35]">{sub.author_surname}, {sub.author_name}</p></div>
                      <div><p className="text-[#8892a8] font-600 uppercase tracking-wide mb-0.5">Supervisor</p><p className="text-[#0f1a35]">{sub.supervisor_surname}, {sub.supervisor_name}</p></div>
                      <div><p className="text-[#8892a8] font-600 uppercase tracking-wide mb-0.5">ORCID</p><p className="text-[#0f1a35] font-mono-data">{sub.orcid || '—'}</p></div>
                      <div><p className="text-[#8892a8] font-600 uppercase tracking-wide mb-0.5">Access</p><p className="text-[#0f1a35]">{sub.access_rights} · {sub.license}</p></div>
                      <div><p className="text-[#8892a8] font-600 uppercase tracking-wide mb-0.5">Department</p><p className="text-[#0f1a35]">{sub.department || '—'}</p></div>
                    </div>

                    {/* Committee */}
                    {(sub.committee_member_1 || sub.committee_member_2) && (
                      <div>
                        <p className="text-xs font-600 text-[#4a5578] uppercase tracking-wide mb-1.5">Examination Committee</p>
                        <div className="flex flex-wrap gap-2">
                          {[sub.committee_member_1, sub.committee_member_2, sub.committee_member_3].filter(Boolean).map((m: string, i: number) => (
                            <span key={`cm-${i}`} className="text-xs bg-[#f7f8fc] border border-[#e2e6f0] rounded-lg px-2.5 py-1 text-[#4a5578]">{m}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Keywords section */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-600 text-[#4a5578] uppercase tracking-wide">Keywords</p>
                        <button
                          onClick={() => setEditingKeywordsId(isEditingKw ? null : sub.id)}
                          className="text-xs text-[#1a2e5a] hover:underline font-600"
                        >
                          {isEditingKw ? 'Done editing' : 'Edit keywords'}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {currentKws.map((kw: string) => (
                          <span key={`kw-${kw}`} className="inline-flex items-center gap-1 text-xs bg-[#1a2e5a]/6 text-[#1a2e5a] border border-[#1a2e5a]/15 rounded-full px-2.5 py-0.5 font-500">
                            {kw}
                            {isEditingKw && (
                              <button onClick={() => toggleEditedKeyword(sub.id, kw)} className="text-[#1a2e5a]/50 hover:text-red-500 ml-0.5">
                                <X size={10} />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                      {isEditingKw && (
                        <div className="border border-[#e2e6f0] rounded-xl p-3 bg-[#f7f8fc]">
                          <div className="relative mb-2">
                            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8892a8]" />
                            <input
                              type="text"
                              placeholder="Search keywords..."
                              value={kwSearch}
                              onChange={e => setKwSearch(e.target.value)}
                              className="w-full pl-7 pr-3 py-1.5 text-xs border border-[#e2e6f0] rounded-lg bg-white focus:outline-none focus:border-[#1a2e5a]"
                            />
                          </div>
                          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                            {filteredKws.map((kw) => (
                              <button
                                key={`opt-${kw}`}
                                onClick={() => toggleEditedKeyword(sub.id, kw)}
                                className={`text-xs px-2.5 py-0.5 rounded-full border font-500 transition-all ${
                                  currentKws.includes(kw)
                                    ? 'bg-[#1a2e5a] text-white border-[#1a2e5a]'
                                    : 'bg-white text-[#4a5578] border-[#e2e6f0] hover:border-[#1a2e5a]'
                                }`}
                              >
                                {kw}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Send back comment */}
                    {isSendingBack && (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                        <p className="text-xs font-600 text-red-800 mb-2">Reason for sending back (required)</p>
                        <textarea
                          value={sendBackComment}
                          onChange={e => setSendBackComment(e.target.value)}
                          placeholder="Explain what the student needs to revise or correct..."
                          rows={3}
                          className="w-full text-xs border border-red-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-red-400 resize-none"
                        />
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex items-center gap-3 pt-2 border-t border-[#e2e6f0]">
                      <button
                        onClick={() => handleApprove(sub.id)}
                        disabled={isProcessing}
                        className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-600 px-4 py-2.5 rounded-xl transition-all disabled:opacity-50"
                      >
                        {approvingId === sub.id ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                        Approve & Publish
                      </button>
                      {!isSendingBack ? (
                        <button
                          onClick={() => setSendBackId(sub.id)}
                          disabled={isProcessing}
                          className="flex items-center gap-2 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 text-xs font-600 px-4 py-2.5 rounded-xl transition-all disabled:opacity-50"
                        >
                          <MessageSquare size={13} />
                          Send Back
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => handleSendBack(sub.id)}
                            disabled={isProcessing}
                            className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-600 px-4 py-2.5 rounded-xl transition-all disabled:opacity-50"
                          >
                            {isProcessing ? <Loader2 size={13} className="animate-spin" /> : <MessageSquare size={13} />}
                            Confirm Send Back
                          </button>
                          <button
                            onClick={() => { setSendBackId(null); setSendBackComment(''); }}
                            className="text-xs text-[#8892a8] hover:text-[#4a5578] px-3 py-2.5"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}